import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, ChevronLeft, ChevronRight, Flame } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CATEGORIES } from "@/data/products";
import { useProducts } from "@/hooks/useProducts";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { customerWishlistAdd, customerWishlistRemove } from "@/lib/api";
import { PixelPop } from "@/components/PixelBurst";
import { optimizedImg, srcSetFor } from "@/lib/img";

/* hash → small int (3..9) for deterministic starting stock per product */
const initialStockFor = (id) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return 4 + (h % 6); // 4–9
};

/* Hook: returns a small "remaining stock" number that ticks down over the
 * session for scarcity nudges. Starts deterministic per product, persists in
 * sessionStorage, decrements by 1 every 28–42s, never goes below 2. */
function useScarcityStock(productId, enabled) {
  const [stock, setStock] = useState(() => {
    if (!enabled || typeof window === "undefined") return null;
    const key = `st-stock-${productId}`;
    const saved = sessionStorage.getItem(key);
    if (saved) return parseInt(saved, 10);
    const initial = initialStockFor(productId);
    sessionStorage.setItem(key, String(initial));
    return initial;
  });

  useEffect(() => {
    if (!enabled || stock == null) return;
    const tickMs = 28000 + Math.floor(Math.random() * 14000);
    const t = setTimeout(() => {
      setStock((prev) => {
        if (prev == null) return prev;
        const next = Math.max(2, prev - 1);
        sessionStorage.setItem(`st-stock-${productId}`, String(next));
        return next;
      });
    }, tickMs);
    return () => clearTimeout(t);
  }, [stock, productId, enabled]);

  return stock;
}

function ProductCard({ p, idx }) {
  const { add } = useCart();
  const { customer } = useCustomerAuth();
  const navigate = useNavigate();
  const [fav, setFav] = useState(false);
  const [popCounter, setPopCounter] = useState(null);
  const sizes = p.sizes || [];
  const needsSize = sizes.length > 0;
  const [size, setSize] = useState(null);
  const [sizeError, setSizeError] = useState(0);
  const showScarcity = p.badge === "BEST SELLER" || p.badge === "LIMITED";
  const stock = useScarcityStock(p.id, showScarcity);

  // Toggle wishlist heart: persists to server for signed-in customers; for
  // guests we redirect to login (their selection is preserved by ?next=).
  const toggleFav = async () => {
    if (!customer) {
      toast("Sign in to save favourites", {
        action: {
          label: "Sign in",
          onClick: () => navigate("/account/login"),
        },
      });
      return;
    }
    const next = !fav;
    setFav(next);
    try {
      if (next) await customerWishlistAdd(p.id);
      else await customerWishlistRemove(p.id);
    } catch (e) {
      // Revert on failure.
      setFav(!next);
      toast.error("Couldn't update wishlist");
    }
  };

  // Inventory map from Shopify webhook. Per-size for apparel, single
  // "DEFAULT" key for accessories. Missing inventory data = treat as in stock
  // (backwards compatibility with products that haven't been synced yet).
  const inv = p.inventory || null;
  const sizeStock = (s) => (inv ? Number(inv[s] ?? 0) : Infinity);
  const isSizeOOS = (s) => inv != null && sizeStock(s) <= 0;
  const totalStock = inv
    ? Object.values(inv).reduce((a, b) => a + Number(b || 0), 0)
    : Infinity;
  const allOOS = inv != null && totalStock <= 0;
  const selectedSizeStock = needsSize && size ? sizeStock(size) : totalStock;
  const lowStock = !allOOS && selectedSizeStock > 0 && selectedSizeStock <= 5;

  const handleAdd = () => {
    if (allOOS) return;
    if (needsSize && !size) {
      setSizeError(Date.now());
      return;
    }
    if (needsSize && size && isSizeOOS(size)) return;
    setSizeError(0);
    add(p, size);
    setPopCounter(Date.now()); // triggers a fresh pixel pop
  };
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 30, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.5, delay: (idx % 8) * 0.06, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -4 }}
      className="group flex flex-col"
      data-testid={`product-card-${p.id}`}
    >
      {/* IMAGE — stepped pixel corners at top, flat bottom, no border */}
      <div className="pixel-step-top relative aspect-[4/5] overflow-hidden">
        <img
          src={optimizedImg(p.image, 800)}
          srcSet={srcSetFor(p.image)}
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          alt={p.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />

        {/* BADGE — pixel ribbon. Bottom on mobile (avoids covering face),
            top-left on md+. Color varies by type. */}
        {p.badge && (
          <div
            data-testid={`product-badge-${p.id}`}
            className={`absolute bottom-3 md:bottom-auto md:top-3 left-0 z-10 font-display uppercase tracking-tight text-[11px] md:text-xs px-2.5 py-1 border-2 border-[#111] shadow-brutal-sm select-none ${
              p.badge === "BEST SELLER"
                ? "bg-[var(--st-maroon)] text-white"
                : p.badge === "NEW DROP"
                ? "bg-[var(--st-mustard)] text-[var(--st-fg)]"
                : p.badge === "LIMITED"
                ? "bg-[var(--st-fg)] text-[var(--st-gold)]"
                : p.badge === "STAFF PICK"
                ? "bg-[var(--st-cream)] text-[var(--st-maroon)]"
                : "bg-[var(--st-orange)] text-white"
            }`}
            style={{ clipPath: "polygon(0 0, 100% 0, 92% 50%, 100% 100%, 0 100%)", paddingRight: "1.25rem" }}
          >
            {p.badge}
          </div>
        )}
      </div>

      {/* NAME + PRICE on cream bg */}
      <div className="mt-3 flex items-start justify-between gap-3">
        <p className="font-display uppercase tracking-tight leading-[0.95] text-sm md:text-base text-[var(--st-fg)]">
          {p.name.split(" ").reduce((acc, w, i, arr) => {
            // break into 2 lines for stacked look
            if (i === Math.ceil(arr.length / 2)) acc.push(<br key={i} />);
            acc.push(w + " ");
            return acc;
          }, [])}
        </p>
        <p className="font-price text-2xl md:text-3xl text-[var(--st-maroon)] shrink-0 whitespace-nowrap leading-none pt-0.5">
          ${p.price}
        </p>
      </div>

      {/* SIZE SELECTOR — only on apparel SKUs with a sizes array */}
      {needsSize && (
        <div className="mt-2.5" data-testid={`product-sizes-${p.id}`}>
          <p className="font-heading uppercase tracking-[0.2em] text-[10px] text-[var(--st-fg)]/60 mb-1.5">
            Size
          </p>
          <div className="flex flex-wrap gap-1.5">
            {sizes.map((s) => {
              const isOn = size === s;
              const oos = isSizeOOS(s);
              return (
                <button
                  key={s}
                  type="button"
                  disabled={oos}
                  onClick={() => {
                    if (oos) return;
                    setSize(s);
                    setSizeError(0);
                  }}
                  data-testid={`product-size-${p.id}-${s}`}
                  aria-pressed={isOn}
                  title={oos ? "Sold out" : undefined}
                  className={`min-w-9 h-9 px-2 border-2 border-[#111] font-display uppercase text-xs transition-colors shadow-brutal-sm ${
                    oos
                      ? "bg-[var(--st-cream)] text-[var(--st-fg)]/30 line-through cursor-not-allowed opacity-60"
                      : isOn
                      ? "bg-[var(--st-maroon)] text-white"
                      : "bg-[var(--st-cream)] text-[var(--st-fg)] hover:bg-[var(--st-mustard)]"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
          {sizeError ? (
            <div
              key={sizeError}
              data-testid={`product-size-error-${p.id}`}
              className="mt-2 inline-flex items-center gap-1.5 bg-[var(--st-maroon)] text-white border-2 border-[#111] px-2.5 py-1.5 font-heading uppercase tracking-[0.18em] text-xs shadow-brutal-sm animate-shake"
            >
              <span className="w-1.5 h-1.5 bg-[var(--st-mustard)]" />
              Pick a size first
            </div>
          ) : null}
        </div>
      )}

      {/* ADD TO CART + heart square — matches design ref layout */}
      <div className="mt-2 flex items-stretch gap-2 relative">
        <button
          onClick={handleAdd}
          disabled={allOOS}
          data-testid={`product-add-${p.id}`}
          className={`relative flex-1 font-display uppercase tracking-tight text-sm md:text-base py-2.5 border-2 border-[#111] transition-colors shadow-brutal-sm overflow-visible ${
            allOOS
              ? "bg-[#11111122] text-[var(--st-fg)]/40 cursor-not-allowed"
              : "bg-[var(--st-gold)] text-[var(--st-fg)] hover:bg-[var(--st-mustard)]"
          }`}
        >
          {allOOS ? "SOLD OUT" : "ADD TO CART"}
          {/* pixel pop fires from centre of the button on each add */}
          {!allOOS && <PixelPop trigger={popCounter} />}
        </button>
        <button
          onClick={toggleFav}
          aria-label="favorite"
          data-testid={`product-fav-${p.id}`}
          className={`w-11 flex items-center justify-center border-2 border-[#111] transition-colors ${
            fav ? "bg-[var(--st-maroon)]" : "bg-[var(--st-cream)] hover:bg-[var(--st-gold)]"
          }`}
        >
          <Heart
            size={14}
            className={fav ? "text-white" : "text-[var(--st-fg)]"}
            fill={fav ? "currentColor" : "none"}
          />
        </button>
      </div>

      {/* SCARCITY COUNTER — only on Best Seller / Limited.
          Below the CTAs so the product image stays clean. */}
      {showScarcity && stock != null && (
        <motion.div
          key={stock}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          data-testid={`product-stock-${p.id}`}
          className="mt-2 inline-flex items-center gap-1.5 self-start font-heading uppercase tracking-[0.2em] text-[10px] md:text-[11px] text-[var(--st-maroon)]"
        >
          <Flame size={11} className="text-[var(--st-orange)]" fill="currentColor" />
          Selling fast — only {stock} left
        </motion.div>
      )}
    </motion.div>
  );
}

export default function ProductGrid() {
  const [active, setActive] = useState("all");
  const { products, loading, byCategory } = useProducts();
  const items = useMemo(() => byCategory(active), [active, products]);
  const activeIdx = CATEGORIES.findIndex((c) => c.id === active);

  const nextCat = () => setActive(CATEGORIES[(activeIdx + 1) % CATEGORIES.length].id);
  const prevCat = () =>
    setActive(CATEGORIES[(activeIdx - 1 + CATEGORIES.length) % CATEGORIES.length].id);

  const headlineLines = ["BORN IN THE", "CLASSROOM,", "RAISED BY THE", "CULTURE"];

  return (
    <section
      id="shop"
      data-testid="product-grid-section"
      className="relative grid-paper-fine py-16 md:py-24 border-b-2 border-[#111] overflow-hidden"
    >
      <div className="cross-marker" style={{ top: 60, left: "8%" }} />
      <div className="cross-marker" style={{ top: 60, right: "8%" }} />
      <div className="cross-marker" style={{ top: "42%", left: "8%" }} />
      <div className="cross-marker" style={{ top: "42%", right: "8%" }} />

      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {/* HUGE HEADLINE — inside the same section, above tabs */}
        <div className="text-center mb-12 md:mb-16">
          {headlineLines.map((line, i) => (
            <motion.h2
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{
                duration: 0.55,
                delay: i * 0.08,
                ease: [0.22, 1, 0.36, 1],
              }}
              className={`font-display uppercase leading-[0.85] tracking-tight text-[10vw] md:text-[6.5vw] lg:text-[5.6vw] ${
                i % 2 === 0 ? "text-[var(--st-maroon)]" : "text-[var(--st-fg)]"
              }`}
            >
              {line}
            </motion.h2>
          ))}
        </div>

        {/* PIXEL CATEGORY TABS with active "wings" */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex flex-wrap items-center justify-center gap-6 md:gap-10 mb-12"
        >
          {CATEGORIES.map((c) => {
            const isActive = active === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                data-testid={`tab-${c.id}`}
                className={`relative font-display uppercase tracking-tight text-xl md:text-2xl transition-all ${
                  isActive ? "" : "text-[var(--st-fg)]/55 hover:text-[var(--st-maroon)]"
                }`}
              >
                <span
                  className={
                    isActive ? "tab-active-wings inline-block relative z-10" : "inline-block px-2"
                  }
                >
                  {c.label}
                </span>
              </button>
            );
          })}
        </motion.div>

        {/* GRID */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 md:gap-x-7 gap-y-10"
          >
            {items.map((p, idx) => (
              <ProductCard key={p.id} p={p} idx={idx} />
            ))}
            {!loading && items.length === 0 && (
              <p className="col-span-full text-center font-body text-[var(--st-fg)]/60 py-12">
                No products in this category yet.
              </p>
            )}
            {loading && items.length === 0 && (
              <p
                className="col-span-full text-center font-body text-[var(--st-fg)]/60 py-12"
                data-testid="products-loading"
              >
                Loading the drop…
              </p>
            )}
          </motion.div>
        </AnimatePresence>

        {/* PIXEL PAGINATION FOOTER — arrows + numbered tracker */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-12 flex items-center justify-center gap-3"
        >
          <button
            onClick={prevCat}
            aria-label="Previous category"
            className="w-10 h-10 border-2 border-[#111] bg-[var(--st-cream)] flex items-center justify-center hover:bg-[var(--st-mustard)] transition-colors shadow-brutal-sm"
          >
            <ChevronLeft size={14} />
          </button>
          <div className="flex items-center gap-3 font-display text-lg md:text-xl text-[var(--st-fg)]/70 px-4">
            {CATEGORIES.map((c, i) => (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                className={`transition-colors ${
                  active === c.id
                    ? "text-[var(--st-maroon)] font-bold underline underline-offset-4 decoration-2"
                    : "hover:text-[var(--st-maroon)]"
                }`}
              >
                {String(i + 1).padStart(2, "0")}
              </button>
            ))}
          </div>
          <button
            onClick={nextCat}
            aria-label="Next category"
            className="w-10 h-10 border-2 border-[#111] bg-[var(--st-mustard)] flex items-center justify-center hover:bg-[var(--st-gold)] transition-colors shadow-brutal-sm"
          >
            <ChevronRight size={14} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
